import React, { useEffect, useMemo, useState } from 'react';
import {
  Archive,
  ArrowDown,
  ArrowUp,
  BookOpenText,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Command,
  ClipboardList,
  Crown,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  MessageSquareText,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Pin,
  PinOff,
  Plus,
  Search,
  Settings2,
  SlidersHorizontal,
  ShieldCheck,
  Sun,
  Users,
  X,
} from 'lucide-react';
import { ChatSession, GeneratedCampaign, TeamMemberRole, User } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { Logo } from './Logo';
import { TranslationKey, useTranslation } from '../i18n/translations';

type Tab = 'dashboard' | 'builder' | 'saved' | 'library' | 'insights' | 'chat' | 'team' | 'account' | 'admin';

interface RecentItem {
  key: string;
  title: string;
  date: string;
  kind: 'campaign' | 'chat';
  campaign?: GeneratedCampaign;
  session?: ChatSession;
}

interface SidebarProps {
  activeTab: Tab;
  setActiveTab: (tab: Tab) => void;
  currentUser: User | null;
  savedCampaigns: GeneratedCampaign[];
  currentCampaignId?: string | null;
  onSelectCampaign: (campaign: GeneratedCampaign) => void;
  chatSessions: ChatSession[];
  activeChatSessionId?: string | null;
  onSelectChatSession: (session: ChatSession) => void;
  onNewProject: () => void;
  onOpenPricing: () => void;
  onLogout: () => void;
  onOpenCommandPalette: () => void;
  onOpenProfile: () => void;
  isMobileOpen: boolean;
  onCloseMobile: () => void;
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
  canCreateCampaign: boolean;
  workspaceRole?: TeamMemberRole;
}

const NAV_GROUPS: Array<{
  key: 'workspace' | 'intelligence';
  label: string;
  items: Array<{ tab: Tab; labelKey: TranslationKey; icon: React.ElementType }>;
}> = [
  {
    key: 'workspace',
    label: 'مساحة العمل',
    items: [
      { tab: 'dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard },
      { tab: 'builder', labelKey: 'nav.builder', icon: ClipboardList },
      { tab: 'saved', labelKey: 'nav.saved', icon: Archive },
      { tab: 'library', labelKey: 'nav.library', icon: BookOpenText },
      { tab: 'team', labelKey: 'nav.team', icon: Users },
    ],
  },
  {
    key: 'intelligence',
    label: 'الذكاء والتحليلات',
    items: [
      { tab: 'insights', labelKey: 'nav.insights', icon: BarChart3 },
      { tab: 'chat', labelKey: 'nav.chat', icon: MessageSquare },
    ],
  },
];

const NAV_ITEMS = NAV_GROUPS.flatMap((group) => group.items);

export const Sidebar: React.FC<SidebarProps> = ({
  activeTab,
  setActiveTab,
  currentUser,
  savedCampaigns,
  currentCampaignId,
  onSelectCampaign,
  chatSessions,
  activeChatSessionId,
  onSelectChatSession,
  onNewProject,
  onOpenPricing,
  onLogout,
  onOpenCommandPalette,
  onOpenProfile,
  isMobileOpen,
  onCloseMobile,
  isCollapsed,
  onToggleCollapsed,
  canCreateCampaign,
  workspaceRole = 'client',
}) => {
  const { theme, toggleTheme } = useTheme();
  const { t, tx } = useTranslation();
  const [historyQuery, setHistoryQuery] = useState('');
  const [recentOpen, setRecentOpen] = useState(true);
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [favoriteTabs, setFavoriteTabs] = useState<Tab[]>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('istihdaf_sidebar_favorites') || '[]');
      return Array.isArray(saved) ? saved : [];
    } catch { return []; }
  });
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('istihdaf_sidebar_groups') || '{}');
      return { workspace: saved.workspace !== false, intelligence: saved.intelligence !== false };
    } catch { return { workspace: true, intelligence: true }; }
  });
  const isAdmin = currentUser?.role === 'admin' || (currentUser?.adminAccess?.permissions?.length ?? 0) > 0;
  const planLabel = currentUser?.plan === 'free' ? t('nav.planFree') : currentUser?.plan === 'pro' ? 'Pro' : 'Agency';
  const creditsPercent = currentUser?.plan === 'free' ? Math.min(100, Math.max(6, ((currentUser?.credits || 0) / 3) * 100)) : 100;
  const allowedTabs = useMemo(() => {
    if (workspaceRole === 'client') return new Set<Tab>(['dashboard', 'saved', 'team', 'account']);
    if (workspaceRole === 'copywriter') return new Set<Tab>(['dashboard', 'saved', 'library', 'team', 'account']);
    return new Set<Tab>(['dashboard', 'builder', 'saved', 'library', 'insights', 'chat', 'team', 'account', 'admin']);
  }, [workspaceRole]);
  const visibleGroups = useMemo(() => NAV_GROUPS.map((group) => ({ ...group, items: group.items.filter((item) => allowedTabs.has(item.tab) && (item.tab !== 'builder' || canCreateCampaign)) })).filter((group) => group.items.length > 0), [allowedTabs, canCreateCampaign]);

  useEffect(() => {
    try { localStorage.setItem('istihdaf_sidebar_groups', JSON.stringify(openGroups)); } catch { /* optional UI preference */ }
  }, [openGroups]);

  useEffect(() => {
    try { localStorage.setItem('istihdaf_sidebar_favorites', JSON.stringify(favoriteTabs)); } catch { /* optional UI preference */ }
  }, [favoriteTabs]);

  useEffect(() => {
    const activeGroup = NAV_GROUPS.find((group) => group.items.some((item) => item.tab === activeTab)
      || (activeTab === 'admin' && group.key === 'intelligence'));
    if (activeGroup && !openGroups[activeGroup.key]) setOpenGroups((current) => ({ ...current, [activeGroup.key]: true }));
  }, [activeTab]);

  const recentItems = useMemo<RecentItem[]>(() => {
    const campaigns: RecentItem[] = savedCampaigns.map((campaign) => ({
      key: `campaign_${campaign.id}`,
      title: campaign.projectInfo.projectName,
      date: campaign.createdAt,
      kind: 'campaign',
      campaign,
    }));
    const chats: RecentItem[] = chatSessions.map((session) => ({
      key: `chat_${session.id}`,
      title: session.title,
      date: session.updated_at,
      kind: 'chat',
      session,
    }));
    return [...campaigns, ...chats]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 8);
  }, [savedCampaigns, chatSessions]);

  const filteredHistory = useMemo(() => {
    const query = historyQuery.trim().toLowerCase();
    return query ? recentItems.filter((item) => item.title.toLowerCase().includes(query)) : recentItems;
  }, [historyQuery, recentItems]);

  const handleNav = (tab: Tab) => {
    setActiveTab(tab);
    onCloseMobile();
  };

  const toggleFavorite = (tab: Tab) => {
    setFavoriteTabs((current) => current.includes(tab) ? current.filter((item) => item !== tab) : [...current, tab]);
  };

  const moveFavorite = (tab: Tab, direction: -1 | 1) => {
    setFavoriteTabs((current) => {
      const index = current.indexOf(tab);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  return (
    <>
      {isMobileOpen && (
        <button
          type="button"
          className="sidebar-mobile-backdrop fixed inset-0 bg-slate-950/45 z-40 animate-fadeIn cursor-default"
          onClick={onCloseMobile}
          aria-label={t('nav.closeMenu')}
        />
      )}

      <aside
        className={`istihdaf-sidebar ${isMobileOpen ? 'is-mobile-open' : ''} ${isCollapsed ? 'is-collapsed' : ''} fixed inset-y-0 start-0 z-50 flex flex-col border-e lg:translate-x-0`}
      >
        <div className="sidebar-brand flex items-center justify-between h-[76px] px-4 shrink-0">
          <button onClick={() => handleNav('dashboard')} className="flex items-center gap-3 min-w-0">
            <Logo className="w-9 h-9" tone="light" />
            <span className="min-w-0 text-start">
              <strong className="block text-[15px] font-bold tracking-tight text-white">{tx('استهداف')}</strong>
              <small className="block text-[9px] uppercase tracking-[0.16em] text-white/45 mt-0.5">{tx('ذكاء الحملات متعدد المجالات')}</small>
            </span>
          </button>
          <button onClick={onCloseMobile} className="sidebar-mobile-close icon-button-dark" aria-label={t('nav.closeMenu')}>
            <X className="w-4.5 h-4.5" />
          </button>
          <button
            onClick={onToggleCollapsed}
            className="sidebar-collapse-button"
            aria-label={isCollapsed ? tx('فتح القائمة الجانبية') : tx('طي القائمة الجانبية')}
            title={isCollapsed ? tx('فتح القائمة الجانبية') : tx('طي القائمة الجانبية')}
          >
            {isCollapsed ? <PanelLeftOpen className="rtl-mirror" /> : <PanelLeftClose className="rtl-mirror" />}
          </button>
        </div>

        {canCreateCampaign && <div className="px-3 pb-3">
          <button
            onClick={() => { onNewProject(); onCloseMobile(); }}
            className="sidebar-primary-action"
            title={isCollapsed ? t('nav.newProject') : undefined}
          >
            <Plus className="w-4 h-4" />
            <span>{t('nav.newProject')}</span>
          </button>
        </div>}

        <div className="px-3 pb-4">
          <button onClick={onOpenCommandPalette} className="sidebar-search" title={isCollapsed ? t('nav.quickSearch') : undefined}>
            <Search className="w-4 h-4" />
            <span className="flex-1 text-start">{t('nav.quickSearch')}</span>
            <kbd><Command className="w-3 h-3" /> K</kbd>
          </button>
        </div>

        <nav className="sidebar-nav px-3 space-y-4" aria-label={tx('التنقل الرئيسي')}>
          <div className="sidebar-nav-tools">
            <span>{tx('التنقل')}</span>
            <button type="button" onClick={() => setIsCustomizing((value) => !value)} className={isCustomizing ? 'is-active' : ''} aria-pressed={isCustomizing} title={tx('تخصيص العناصر المفضلة')}><SlidersHorizontal /><span>{tx('تخصيص')}</span></button>
          </div>

          {favoriteTabs.length > 0 && !isCollapsed && (
            <div className="sidebar-favorites">
              <div className="sidebar-favorites-heading"><Pin /><span>{tx('المفضلة')}</span></div>
              <div className="sidebar-favorites-list">
                {favoriteTabs.map((tab, index) => {
                  const item = NAV_ITEMS.find((candidate) => candidate.tab === tab && allowedTabs.has(candidate.tab));
                  if (!item) return null;
                  const Icon = item.icon;
                  return (
                    <div key={tab} className="sidebar-favorite-row">
                      <button type="button" onClick={() => handleNav(tab)} className={`sidebar-nav-item ${activeTab === tab ? 'is-active' : ''}`}><Icon /><span>{t(item.labelKey)}</span></button>
                      {isCustomizing && <span className="sidebar-favorite-order"><button type="button" disabled={index === 0} onClick={() => moveFavorite(tab, -1)} aria-label={tx('تحريك لأعلى')}><ArrowUp /></button><button type="button" disabled={index === favoriteTabs.length - 1} onClick={() => moveFavorite(tab, 1)} aria-label={tx('تحريك لأسفل')}><ArrowDown /></button><button type="button" onClick={() => toggleFavorite(tab)} aria-label={tx('إزالة من المفضلة')}><PinOff /></button></span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {visibleGroups.map((group) => (
            <div key={group.key} className={`sidebar-nav-group ${openGroups[group.key] || isCollapsed ? 'is-open' : 'is-closed'}`}>
              <button type="button" className="sidebar-group-toggle" onClick={() => setOpenGroups((current) => ({ ...current, [group.key]: !current[group.key] }))} aria-expanded={openGroups[group.key]} title={tx(group.label)}>
                <span>{tx(group.label)}</span>
                {openGroups[group.key] ? <ChevronUp /> : <ChevronDown />}
              </button>
              {(openGroups[group.key] || isCollapsed) && <div className="space-y-1 sidebar-group-items">
                {group.items.map(({ tab, labelKey, icon: Icon }) => {
                  const isActive = activeTab === tab;
                  return (
                    <div key={tab} className="sidebar-nav-row">
                      <button onClick={() => handleNav(tab)} className={`sidebar-nav-item ${isActive ? 'is-active' : ''}`} title={isCollapsed ? t(labelKey) : undefined}>
                        <Icon className="w-[17px] h-[17px]" />
                        <span className="flex-1 text-start">{t(labelKey)}</span>
                        {tab === 'insights' && currentUser?.plan === 'free' && <span className="sidebar-plan-badge">PRO</span>}
                        {tab === 'team' && currentUser?.plan !== 'agency' && <span className="sidebar-plan-badge">AGENCY</span>}
                        {tab === 'saved' && savedCampaigns.length > 0 && <span className="sidebar-count">{savedCampaigns.length}</span>}
                      </button>
                      {isCustomizing && !isCollapsed && <button type="button" onClick={() => toggleFavorite(tab)} className={`sidebar-pin-button ${favoriteTabs.includes(tab) ? 'is-pinned' : ''}`} aria-label={favoriteTabs.includes(tab) ? tx('إزالة من المفضلة') : tx('تثبيت في المفضلة')}>{favoriteTabs.includes(tab) ? <PinOff /> : <Pin />}</button>}
                    </div>
                  );
                })}
                {isAdmin && group.label === 'الذكاء والتحليلات' && (
                  <button onClick={() => handleNav('admin')} className={`sidebar-nav-item ${activeTab === 'admin' ? 'is-active' : ''}`} title={isCollapsed ? t('nav.admin') : undefined}>
                    <ShieldCheck className="w-[17px] h-[17px]" />
                    <span className="flex-1 text-start">{t('nav.admin')}</span>
                  </button>
                )}
              </div>}
            </div>
          ))}
        </nav>

        <div className="sidebar-recents flex-1 min-h-0 flex flex-col mt-5 px-3">
          <button className="sidebar-recent-toggle" onClick={() => setRecentOpen((value) => !value)} aria-expanded={recentOpen}>
            <span>{tx('الأحدث')}</span>
            {recentOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>

          {recentOpen && (
            <div className="flex-1 min-h-0 flex flex-col animate-fadeIn">
              {recentItems.length > 3 && (
                <label className="sidebar-mini-search">
                  <Search className="w-3.5 h-3.5" />
                  <input value={historyQuery} onChange={(event) => setHistoryQuery(event.target.value)} placeholder={t('nav.searchProjects')} />
                </label>
              )}
              <div className="overflow-y-auto space-y-0.5 pb-2">
                {filteredHistory.length === 0 ? (
                  <p className="px-3 py-4 text-[11px] text-white/35">{t('nav.noSavedProjects')}</p>
                ) : filteredHistory.map((item) => {
                  const isCurrent = item.kind === 'campaign'
                    ? item.campaign?.id === currentCampaignId
                    : item.session?.id === activeChatSessionId && activeTab === 'chat';
                  const Icon = item.kind === 'chat' ? MessageSquareText : Archive;
                  return (
                    <button
                      key={item.key}
                      className={`sidebar-recent-item ${isCurrent ? 'is-current' : ''}`}
                      onClick={() => {
                        item.kind === 'campaign' ? onSelectCampaign(item.campaign!) : onSelectChatSession(item.session!);
                        onCloseMobile();
                      }}
                      title={item.title}
                    >
                      <Icon className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{item.title}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="sidebar-footer shrink-0 p-3 space-y-2">
          {currentUser && (
            <button onClick={onOpenPricing} className="sidebar-plan-card" title={isCollapsed ? planLabel : undefined}>
              <span className="flex items-center justify-between gap-2 text-[11px] font-semibold">
                <span className="flex items-center gap-2"><Crown className="w-3.5 h-3.5" /> {planLabel}</span>
                <span>{`${currentUser.credits} ${t('nav.credits')}`}</span>
              </span>
              <span className="sidebar-plan-track"><span style={{ width: `${creditsPercent}%` }} /></span>
            </button>
          )}

          {currentUser && (
            <div className="sidebar-profile-row">
              <button onClick={onOpenProfile} className={`flex items-center gap-2.5 flex-1 min-w-0 text-start ${activeTab === 'account' ? 'is-active' : ''}`}>
                {currentUser.avatarUrl ? (
                  <img src={currentUser.avatarUrl} alt={currentUser.name} className="w-8 h-8 rounded-lg object-cover shrink-0" />
                ) : (
                  <span className="sidebar-avatar">{currentUser.name.charAt(0)}</span>
                )}
                <span className="min-w-0 flex-1">
                  <strong className="block text-[11px] font-semibold text-white truncate">{currentUser.name}</strong>
                  <small className="block text-[9px] text-white/40 truncate">{currentUser.email}</small>
                </span>
              </button>
              <button onClick={onOpenProfile} className="icon-button-dark" title={t('profile.title')} aria-label={t('profile.title')}><Settings2 className="w-4 h-4" /></button>
              <button onClick={toggleTheme} className="icon-button-dark" title={theme === 'dark' ? t('nav.lightMode') : t('nav.darkMode')} aria-label={theme === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}>
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <button onClick={onLogout} className="icon-button-dark hover:!text-rose-300" title={t('nav.logout')} aria-label={t('nav.logout')}><LogOut className="w-4 h-4" /></button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};
